import React, { useContext, useEffect, useState } from "react";
import Cookies from "js-cookie";
import axios from "axios";
import { AccountContext } from "../contexts/accountContext";
import buildLink from "../urls";
import ButtonLoader from "../components/LoadingError/buttonLoader";
import { useHistory } from "react-router-dom";
import "./login.css";

// Same wording `handleError` uses elsewhere in the admin, but shown inline on
// the form instead of in a snackbar: the message needs to stay on screen while
// the person fixes the field it is about.
const messageFor = (error) => {
  if (error.response) {
    return error.response.data?.message || "Server error";
  }
  if (error.request) {
    return "No response received from the server";
  }
  return "Error fetching the request";
};

const Login = ({ isLoggedIn }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [formError, setFormError] = useState("");
  // Only a rejected credential should mark the fields themselves as wrong — a
  // dropped connection is not the person's typing.
  const [fieldsRejected, setFieldsRejected] = useState(false);
  const { dispatch } = useContext(AccountContext);
  const history = useHistory();

  const login = async () => {
    dispatch({ type: "setLoading", payload: true });
    const requestData = {
      username: username,
      password: password,
    };

    try {
      setLoading(true);
      const response = await axios.post(buildLink("admin_login"), requestData);
      if (response.status === 200) {
        const data = response.data.data;
        dispatch({ type: "setLoged", payload: true });
        dispatch({ type: "setAdmin", payload: true });
        dispatch({ type: "setAdminToken", payload: data.token });
        Cookies.set("token", data.token, { expires: 1 });
        history.push("/orders");
      } else {
        dispatch({ type: "setLoged", payload: false });
      }
    } catch (error) {
      setFormError(messageFor(error));
      setFieldsRejected(Boolean(error.response));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const token = Cookies.get("token");
    if (token) {
      dispatch({ type: "setLoged", payload: true });
      dispatch({ type: "setAdmin", payload: true });
      dispatch({ type: "setAdminToken", payload: token });
    } else {
      dispatch({ type: "setLoged", payload: false });
      dispatch({ type: "setAdmin", payload: false });
    }
  }, [dispatch]);

  // The form is `noValidate` so the errors read the same as a rejected login
  // rather than arriving in a browser bubble — which means the empty case has
  // to be caught here, or it would post a blank username.
  const submitHandler = (e) => {
    e.preventDefault();
    if (!username.trim() || !password) {
      setFormError("Enter your username and password.");
      setFieldsRejected(true);
      return;
    }
    setFormError("");
    setFieldsRejected(false);
    login();
  };

  // Clear the error as soon as the person starts correcting it, so the fields
  // do not stay red while they retype.
  const clearError = () => {
    if (formError) {
      setFormError("");
      setFieldsRejected(false);
    }
  };

  useEffect(() => {
    // Check if user is logged in and redirect to "/orders"
    if (isLoggedIn) {
      history.push("/orders");
    }
  }, [isLoggedIn, history]);

  // Caps lock is the most common cause of a "the password is wrong" call, so
  // it is checked on every key event the password field sees.
  const trackCapsLock = (e) => {
    if (typeof e.getModifierState === "function") {
      setCapsLock(e.getModifierState("CapsLock"));
    }
  };

  return (
    <div className="tfmlog-page">
      {/* The sky and the furrowed field the card stands on. Decorative: the
          same three shapes as the logo mark, drawn at full size. */}
      <div className="tfmlog-sky" aria-hidden="true" />
      <div className="tfmlog-ground" aria-hidden="true" />

      <div className="tfmlog-stage">
        <div className="tfmlog-card">
          <img
            className="tfmlog-logo"
            src="/images/fulllogo.png"
            alt="Tripoli Fresh Market Wholesale"
          />
          <hr className="tfmlog-rule" />
          <span className="tfmlog-mono tfmlog-eyebrow">Staff access</span>
          <h1 className="tfmlog-title">Sign in</h1>
          <p className="tfmlog-sub">
            The dashboard for orders, prices and stock.
          </p>

          <form onSubmit={submitHandler} noValidate>
            <div className="tfmlog-fields">
              {formError && (
                <div className="tfmlog-error" role="alert">
                  <svg
                    width="15"
                    height="15"
                    viewBox="0 0 16 16"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      cx="8"
                      cy="8"
                      r="7"
                      stroke="currentColor"
                      strokeWidth="1.5"
                    />
                    <path
                      d="M8 4.6v4.2M8 11.2v.6"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                    />
                  </svg>
                  <span>{formError}</span>
                </div>
              )}

              <div className="tfmlog-field">
                <label
                  className="tfmlog-mono tfmlog-label"
                  htmlFor="tfmlog-username"
                >
                  Username or email
                </label>
                <div
                  className={`tfmlog-inputwrap${
                    fieldsRejected ? " is-invalid" : ""
                  }`}
                >
                  <input
                    id="tfmlog-username"
                    className="tfmlog-input"
                    type="text"
                    autoComplete="username"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck="false"
                    value={username}
                    onChange={(e) => {
                      setUsername(e.target.value);
                      clearError();
                    }}
                    required
                  />
                </div>
              </div>

              <div className="tfmlog-field">
                <label
                  className="tfmlog-mono tfmlog-label"
                  htmlFor="tfmlog-password"
                >
                  Password
                </label>
                <div
                  className={`tfmlog-inputwrap${
                    fieldsRejected ? " is-invalid" : ""
                  }`}
                >
                  <input
                    id="tfmlog-password"
                    className="tfmlog-input"
                    type={showPassword ? "text" : "password"}
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      clearError();
                    }}
                    onKeyUp={trackCapsLock}
                    onKeyDown={trackCapsLock}
                    onBlur={() => setCapsLock(false)}
                    aria-describedby={
                      capsLock ? "tfmlog-caps" : undefined
                    }
                    required
                  />
                  <button
                    type="button"
                    className="tfmlog-reveal"
                    onClick={() => setShowPassword((v) => !v)}
                    aria-pressed={showPassword}
                    aria-controls="tfmlog-password"
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                {capsLock && (
                  <p className="tfmlog-hint" id="tfmlog-caps">
                    <svg
                      width="13"
                      height="13"
                      viewBox="0 0 16 16"
                      fill="none"
                      aria-hidden="true"
                    >
                      <path
                        d="M8 2.2 2.6 7.6h2.9v3.1h5v-3.1h2.9L8 2.2Z"
                        stroke="currentColor"
                        strokeWidth="1.4"
                        strokeLinejoin="round"
                      />
                      <rect
                        x="5.5"
                        y="12.2"
                        width="5"
                        height="1.9"
                        rx="0.5"
                        stroke="currentColor"
                        strokeWidth="1.4"
                      />
                    </svg>
                    Caps lock is on.
                  </p>
                )}
              </div>

              <button
                type="submit"
                disabled={loading}
                className="tfmlog-submit"
              >
                {loading ? <ButtonLoader /> : <>Sign in</>}
              </button>
            </div>
          </form>

        </div>
      </div>
    </div>
  );
};

export default Login;
